const PROFILE_KEY = "ka_hub_profile_v1";
const EXP_KEY = "ka_hub_experience_v1";

const ROLE_LABELS = {
  baker: "Baker",
  froster: "Froster",
  decorator: "Decorator",
};

export { ROLE_LABELS };

export function loadHubProfile() {
  try {
    const raw = localStorage.getItem(PROFILE_KEY);
    if (!raw) return { name: "", role: "", phone: "", staffId: "" };
    const p = JSON.parse(raw);
    return {
      name: p.name || "",
      role: p.role || "",
      phone: p.phone || "",
      staffId: p.staffId || "",
    };
  } catch {
    return { name: "", role: "", phone: "", staffId: "" };
  }
}

/** Sync hub profile after phone login / sign-up. */
export function applyKitchenStaffSession(staff) {
  if (!staff) return;
  const prev = loadHubProfile();
  saveHubProfile({
    name: staff.name || prev.name,
    role: staff.primary_role || prev.role,
    phone: staff.phone || prev.phone,
    staffId: staff.id || prev.staffId,
  });
}

export function clearHubProfile() {
  try {
    localStorage.removeItem(PROFILE_KEY);
  } catch {
    /* ignore */
  }
}

/** True when staff picked a real name on the KA hub (not placeholder). */
export function hasHubProfileName(profile = null) {
  const p = profile || loadHubProfile();
  const name = (p?.name || "").trim();
  return Boolean(name && name.toLowerCase() !== "team");
}

export function hubProfileLoginGateMessage(profile = null) {
  if (hasHubProfileName(profile)) return "";
  return "Pehle apna mobile number se login karo — Kitchen home par.";
}

export function saveHubProfile({ name, role, phone, staffId }) {
  const prev = loadHubProfile();
  localStorage.setItem(
    PROFILE_KEY,
    JSON.stringify({
      name: name ?? prev.name ?? "",
      role: role ?? prev.role ?? "",
      phone: phone ?? prev.phone ?? "",
      staffId: staffId ?? prev.staffId ?? "",
    })
  );
}

function loadExperienceRaw() {
  try {
    const raw = localStorage.getItem(EXP_KEY);
    return raw ? JSON.parse(raw) : { days: {}, streak: 0, lastPrepDate: "", milestones: {} };
  } catch {
    return { days: {}, streak: 0, lastPrepDate: "", milestones: {} };
  }
}

function saveExperienceRaw(data) {
  localStorage.setItem(EXP_KEY, JSON.stringify(data));
}

function dayBucket(data, dateIso) {
  if (!data.days[dateIso]) {
    data.days[dateIso] = { prepSaves: 0, roleOpens: {} };
  }
  return data.days[dateIso];
}

export function recordPrepSave(dateIso) {
  if (!dateIso) return;
  const data = loadExperienceRaw();
  const bucket = dayBucket(data, dateIso);
  bucket.prepSaves = (bucket.prepSaves || 0) + 1;

  const yesterday = shiftIsoDate(dateIso, -1);
  if (data.lastPrepDate === dateIso) {
    /* same day */
  } else if (data.lastPrepDate === yesterday) {
    data.streak = (data.streak || 0) + 1;
  } else if (data.lastPrepDate !== dateIso) {
    data.streak = 1;
  }
  data.lastPrepDate = dateIso;
  saveExperienceRaw(data);
}

export function recordRoleOpen(role, dateIso) {
  if (!role || !dateIso) return;
  const data = loadExperienceRaw();
  const bucket = dayBucket(data, dateIso);
  bucket.roleOpens[role] = (bucket.roleOpens[role] || 0) + 1;
  saveExperienceRaw(data);
}

/** @returns {number|null} newly crossed milestone (50, 80, 100) */
export function recordScoreMilestone(dateIso, score) {
  if (!dateIso || score == null) return null;
  const data = loadExperienceRaw();
  const key = `${dateIso}`;
  if (!data.milestones[key]) data.milestones[key] = [];
  let fresh = null;
  for (const m of [50, 80, 100]) {
    if (score >= m && !data.milestones[key].includes(m)) {
      data.milestones[key].push(m);
      fresh = m;
    }
  }
  if (fresh) saveExperienceRaw(data);
  return fresh;
}

export function getTodayExperience(dateIso) {
  const data = loadExperienceRaw();
  return data.days[dateIso] || { prepSaves: 0, roleOpens: {} };
}

export function getPrepStreak() {
  return loadExperienceRaw().streak || 0;
}

function shiftIsoDate(iso, deltaDays) {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + deltaDays));
  return dt.toISOString().slice(0, 10);
}

/** IST hour bucket for full soft theme shift */
export function getShiftMood() {
  const hour = Number(
    new Intl.DateTimeFormat("en-IN", {
      timeZone: "Asia/Kolkata",
      hour: "numeric",
      hour12: false,
    }).format(new Date())
  );
  if (hour >= 5 && hour < 11) return "morning";
  if (hour >= 11 && hour < 17) return "afternoon";
  if (hour >= 17 && hour < 21) return "evening";
  return "night";
}

export function getTenureLine(profile) {
  if (!profile?.name) return "";
  const streak = getPrepStreak();
  if (streak >= 3) return "Regular shift · prep streak chal raha hai";
  return "Aaj shift me ho · team ke saath";
}

export function buildExperienceSummary(dateIso, profile) {
  const today = getTodayExperience(dateIso);
  const parts = [];
  if (today.prepSaves > 0) parts.push(`${today.prepSaves} prep save`);
  const role = profile?.role;
  if (role && today.roleOpens[role] > 0) {
    parts.push(`${ROLE_LABELS[role] || role} ${today.roleOpens[role]}×`);
  }
  return parts.join(" · ");
}

export function buildBadgePills(readiness, dateIso) {
  const pills = [];
  const streak = getPrepStreak();
  if (streak >= 3) pills.push(`${streak} din prep on time`);
  const score = readiness?.score;
  if (score >= 80) pills.push("Score strong");
  const data = loadExperienceRaw();
  const hit = data.milestones?.[dateIso] || [];
  if (hit.includes(100)) pills.push("100% readiness");
  return pills;
}

export function isManagerHubProfile(profile) {
  return (profile?.name || "").trim().toLowerCase() === "aanchal";
}
